import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, MapPin, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { z } from 'zod';
const contactSchema = z.object({
  firstName: z.string().trim().min(1, 'First name is required').max(100, 'First name must be less than 100 characters'),
  lastName: z.string().trim().min(1, 'Last name is required').max(100, 'Last name must be less than 100 characters'),
  email: z.string().trim().email('Please enter a valid email address').max(255, 'Email must be less than 255 characters').optional().or(z.literal('')),
  phone: z.string().trim().min(1, 'Phone number is required').regex(/^[0-9()\s.+-]+$/, 'Please enter a valid phone number').min(10, 'Phone number must be at least 10 digits').max(20, 'Phone number is too long'),
  product: z.string().optional(),
  message: z.string().trim().max(5000, 'Message must be less than 5000 characters').optional()
});
const productOptions = ['Plantation Wood Shutters', 'Cellular Shades', 'Zebra Shades', 'Roller Shades', 'Patio Screens', 'Sun Screens', 'Faux Wood Blinds', 'Wood Blinds', 'Smart Drapes', 'Roman Shades', 'Other'];
export function Contact() {
  const {
    toast
  } = useToast();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [product, setProduct] = useState('');
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const rawData = {
      firstName: formData.get('firstName') as string || '',
      lastName: formData.get('lastName') as string || '',
      phone: formData.get('phone') as string || '',
      email: formData.get('email') as string || '',
      product,
      message: formData.get('message') as string || ''
    };

    // Validate with zod schema
    const validation = contactSchema.safeParse(rawData);
    if (!validation.success) {
      const firstError = validation.error.errors[0];
      toast({
        title: "Validation Error",
        description: firstError.message,
        variant: "destructive"
      });
      return;
    }
    const validatedData = validation.data;
    setIsSubmitting(true);

    // Sanitize inputs for emailBody template
    const sanitize = (str: string | undefined) => (str || '').replace(/[<>]/g, '').substring(0, 1000);
    const data = {
      firstName: validatedData.firstName,
      lastName: validatedData.lastName,
      email: validatedData.email || '',
      phone: validatedData.phone,
      product: validatedData.product || '',
      message: validatedData.message || '',
      emailBody: `New Contact Form Submission\n\nName: ${sanitize(validatedData.firstName)} ${sanitize(validatedData.lastName)}\nEmail: ${sanitize(validatedData.email) || 'Not provided'}\nPhone: ${sanitize(validatedData.phone)}\nProduct Interest: ${sanitize(validatedData.product) || 'Not specified'}\nMessage: ${sanitize(validatedData.message) || 'No message'}\n\nSubmitted: ${new Date().toLocaleString()}`,
      timestamp: new Date().toISOString(),
      source: 'Website Contact Form'
    };
    try {
      const response = await fetch('https://formspree.io/f/xdabyvpw', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(data)
      });
      if (!response.ok) {
        throw new Error(`Formspree responded with ${response.status}`);
      }
      navigate('/thank-you', { state: { fromForm: true } });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: "Submission Failed",
        description: "Please try again or call 602.691.4066",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  return <section id="contact" className="py-20 md:py-28 bg-secondary/30">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Left Content */}
          <div>
            <span className="inline-block px-4 py-1.5 bg-sage-light text-sage rounded-full text-sm font-medium mb-4">
              Contact Us
            </span>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6">
              Get Your Free Same Day Estimate
            </h2>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">Ready to transform your windows? Contact us for a free same day estimate, we'll help you find the perfect window treatments for your home.</p>

            {/* Contact Info */}
            <div className="space-y-6">
              <a href="tel:6026914066" className="flex items-center gap-4 p-4 bg-card rounded-xl shadow-soft hover:shadow-medium transition-all group">
                <div className="w-12 h-12 rounded-xl bg-sage-light flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Phone className="w-6 h-6 text-sage" />
                </div>
                <div>
                  <div className="font-semibold text-foreground">Call Us</div>
                  <div className="text-muted-foreground">602.691.4066</div>
                </div>
              </a>

              <div className="flex items-center gap-4 p-4 bg-card rounded-xl shadow-soft">
                <div className="w-12 h-12 rounded-xl bg-sage-light flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-sage" />
                </div>
                <div>
                  <div className="font-semibold text-foreground">Service Area</div>
                  <div className="text-muted-foreground">Phoenix & West Valley, Arizona</div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <form onSubmit={handleSubmit} className="bg-card rounded-2xl shadow-medium p-8">
            <div className="grid gap-6">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name *</Label>
                  <Input id="firstName" name="firstName" placeholder="John" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name *</Label>
                  <Input id="lastName" name="lastName" placeholder="Doe" />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" name="email" type="email" placeholder="john@example.com" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone *</Label>
                  <Input id="phone" name="phone" type="tel" placeholder="(602) 555-0123" />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="product">Window Covering Interest</Label>
                <Select value={product} onValueChange={setProduct}>
                  <SelectTrigger aria-label="Select a window covering product">
                    <SelectValue placeholder="Select a product" />
                  </SelectTrigger>
                  <SelectContent>
                    {productOptions.map(option => <SelectItem key={option} value={option.toLowerCase().replace(/\s+/g, '-')}>
                        {option}
                      </SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message">Message</Label>
                <Textarea id="message" name="message" rows={4} placeholder="Tell us about your project..." />
              </div>

              <Button type="submit" size="lg" disabled={isSubmitting} className="w-full gap-2">
                {isSubmitting ? 'Sending...' : 'Send Message'}
                <Send className="w-5 h-5" />
              </Button>
            </div>
          </form>
        </div>
      </div>
    </section>;
}